/**
 * Vendor Score Service
 * 
 * Updated: 2025-12-17 - Migrated to Thalassa API
 * Updated: 2025-12-17 - Added request deduplication and caching
 * Updated: 2025-12-30 - Global rate limiting via deiterateAPI
 */

import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

const VENDOR_SCORE_API = '/v1/vendor-score';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Define interfaces for our data models
export interface VendorScoreMetadata {
  addedDate: string;
  currentScore: number;
  domain: string;
  firstCaptureDate: string;
  latestCaptureDate: string;
  vendor?: string;
}

export interface VendorScoreHistory {
  captureDate: string;
  capturedScore: number;
  domain: string;
  previousCaptureDate: string;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

class VendorScoreService {
  // In-flight request deduplication
  private pendingRequests = new Map<string, Promise<any>>();
  // Response cache
  private cache = new Map<string, CacheEntry<any>>();

  private getCacheKey(method: string, domain: string, params?: string): string {
    return `${method}:${domain}:${params || ''}`;
  }

  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (entry && Date.now() - entry.timestamp < CACHE_TTL_MS) {
      return entry.data;
    }
    if (entry) {
      this.cache.delete(key); // Clean up expired entry
    }
    return null;
  }

  private setCache<T>(key: string, data: T): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  private async deduplicatedRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // Check cache first
    const cached = this.getFromCache<T>(key);
    if (cached !== null) {
      return cached;
    }

    // Check if request is already in-flight
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending;
    }

    // Make new request
    const promise = requestFn()
      .then(result => {
        this.setCache(key, result);
        return result;
      })
      .finally(() => {
        this.pendingRequests.delete(key);
      });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  private normalizeDomain(domain: string): string {
    if (!domain) return '';
    try {
      const urlStr = domain.startsWith('http') ? domain : `http://${domain}`;
      const url = new URL(urlStr);
      return url.hostname.replace(/^www\./, '').toLowerCase();
    } catch {
      return domain
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .toLowerCase()
        .trim();
    }
  }

  /**
   * Get the current vendor score via Thalassa API
   */
  async getVendorScore(
    domain: string,
    maxAgeMs: number = 7 * 24 * 60 * 60 * 1000,
    bypassCache?: boolean
  ): Promise<{ score: number; cached: true; history?: Array<{ date: Date; score: number }> } | null> {
    const normalizedDomain = this.normalizeDomain(domain);
    if (!normalizedDomain) {
      console.error('[VendorScore] Invalid domain');
      return null;
    }

    // Skip deduplication if bypassing cache
    if (bypassCache) {
      return this._fetchVendorScore(normalizedDomain, maxAgeMs, true);
    }

    const cacheKey = this.getCacheKey('score', normalizedDomain, `${maxAgeMs}`);
    return this.deduplicatedRequest(cacheKey, () => 
      this._fetchVendorScore(normalizedDomain, maxAgeMs, false)
    );
  }

  private async _fetchVendorScore(
    normalizedDomain: string,
    maxAgeMs: number,
    bypassCache: boolean
  ): Promise<{ score: number; cached: true; history?: Array<{ date: Date; score: number }> } | null> {
    try {
      const queryParams = new URLSearchParams();
      if (maxAgeMs) queryParams.set('maxAgeMs', maxAgeMs.toString());
      if (bypassCache) queryParams.set('bypassCache', 'true');

      const response = await deiterateAPI.GET(
        `${VENDOR_SCORE_API}/${encodeURIComponent(normalizedDomain)}?${queryParams}`,
        { mode: 'cors', backend: 'thalassa', priority: 0 } // Lower priority for vendor score requests
      );

      if (response.statusCode !== 200 || !response.score) {
        return null;
      }

      const history = response.history?.map((h: { date: string; score: number }) => ({
        date: new Date(h.date),
        score: h.score
      })) || [];

      return {
        score: response.score,
        cached: true,
        history
      };
    } catch (error) {
      console.error('[VendorScore] Error getting vendor score:', error);
      return null;
    }
  }

  /**
   * Get the score history via Thalassa API
   * Uses request deduplication and caching to prevent API spam
   */
  async getVendorScoreHistory(
    domain: string,
    limit: number = 30
  ): Promise<VendorScoreHistory[]> {
    const normalizedDomain = this.normalizeDomain(domain);
    if (!normalizedDomain) {
      console.error('[VendorScore] Invalid domain');
      return [];
    }

    const cacheKey = this.getCacheKey('history', normalizedDomain, `${limit}`);
    return this.deduplicatedRequest(cacheKey, () => 
      this._fetchVendorScoreHistory(normalizedDomain, limit)
    );
  }

  private async _fetchVendorScoreHistory(
    normalizedDomain: string,
    limit: number
  ): Promise<VendorScoreHistory[]> {
    try {
      const response = await deiterateAPI.GET(
        `${VENDOR_SCORE_API}/${encodeURIComponent(normalizedDomain)}/history?limit=${limit}`,
        { mode: 'cors', backend: 'thalassa', priority: 0 } // Lower priority for vendor score requests
      );

      if (response.statusCode !== 200 || !response.history) {
        return [];
      }

      return response.history;
    } catch (error) {
      console.error('[VendorScore] Error getting score history:', error);
      return [];
    }
  }

  /**
   * Update the vendor score via Thalassa API
   */
  async updateVendorScore(domain: string, score: number, vendor: string = 'upguard'): Promise<void> {
    try {
      console.log('[VendorScore] Updating vendor score via Thalassa API for:', domain);
      
      const normalizedDomain = this.normalizeDomain(domain);
      if (!normalizedDomain) {
        throw new Error('Invalid domain');
      }

      const response = await deiterateAPI.PUT(
        `${VENDOR_SCORE_API}/${encodeURIComponent(normalizedDomain)}`,
        {
          mode: 'cors',
          backend: 'thalassa',
          body: JSON.stringify({ score, vendor }),
          priority: 2 // Higher priority for updates
        }
      );

      if (response.statusCode !== 200) {
        throw new Error(response.error || 'Failed to update vendor score');
      }

      console.log('[VendorScore] Successfully updated vendor score:', response);
    } catch (error) {
      console.error('[VendorScore] Error updating vendor score:', error);
      throw error;
    }
  }
}

export const vendorScoreService = new VendorScoreService();
